/**
 * Auth Store — Anmeldezustand
 *
 * Die Sitzung hängt an einem httpOnly-Cookie, das der Browser bei jeder Anfrage an die
 * gleichnamige Origin mitschickt (`withCredentials` im API-Client). JavaScript kommt an das
 * Token nicht heran — und legt es deshalb auch nirgends ab.
 *
 * Was hier im localStorage bleibt, sind reine Anzeigedaten: Name, Rolle, Kennungen. Sie sind
 * keine Geheimnisse, aber sie werden offline gebraucht: ohne sie wüsste die App beim Start ohne
 * Netz nicht, wen sie vor sich hat, und würde einen angemeldeten Trainer auf die Anmeldemaske
 * werfen.
 *
 * Bis zum 2026-09-02 lag hier das JWT selbst. Zusätzlich kam es beim Microsoft-Login als
 * Query-Parameter an — und damit in die Zugriffsprotokolle von zwei Proxys.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient, getErrorMessage, isNetworkError } from '@/api/client'
import { leseSperrzeitpunkt } from '@/utils/kontosperre'
import { db } from '@/db'
import { useCoursesStore } from '@/stores/courses.store'
import { useSessionsStore } from '@/stores/sessions.store'
import { useRegistrationsStore } from '@/stores/registrations.store'
import { useAttendancesStore } from '@/stores/attendances.store'
import { useGuestsStore } from '@/stores/guests.store'
import { useBillingStore } from '@/stores/billing.store'
import { useParticipantsStore } from '@/stores/participants.store'
import { useInstructorsStore } from '@/stores/instructors.store'
import { usePhotosStore } from '@/stores/photos.store'
import type { CurrentUserResponse, LoginRequest, LoginResponse } from '@/types/api.types'

const STORAGE_KEYS = {
  USER_ID: 'tuscourses_user_id',
  PERSON_ID: 'tuscourses_person_id',
  USER_NAME: 'tuscourses_user_name',
  USER_ROLE: 'tuscourses_user_role',
  LAST_VALIDATED: 'tuscourses_last_validated',
  REDIRECT_URL: 'tuscourses_redirect_url',
} as const

// Validation interval: 5 minutes
const VALIDATION_INTERVAL = 5 * 60 * 1000

/**
 * Schlüssel früherer Fassungen, unter denen das JWT und die Sitzungskennung im Browser lagen.
 * Sie werden beim Start einmalig entfernt: ein Gerät, das die alte Fassung installiert hatte,
 * trägt den Wert sonst weiter mit sich herum, obwohl ihn niemand mehr braucht.
 */
const ALTLASTEN = ['tuscourses_jwt', 'tuscourses_session'] as const

function entferneAltlasten(): void {
  ALTLASTEN.forEach((key) => localStorage.removeItem(key))
}

export const useAuthStore = defineStore('auth', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  entferneAltlasten()

  const userId = ref<number | null>(
    localStorage.getItem(STORAGE_KEYS.USER_ID)
      ? Number(localStorage.getItem(STORAGE_KEYS.USER_ID))
      : null
  )
  const personId = ref<number | null>(
    localStorage.getItem(STORAGE_KEYS.PERSON_ID)
      ? Number(localStorage.getItem(STORAGE_KEYS.PERSON_ID))
      : null
  )
  const userName = ref<string | null>(localStorage.getItem(STORAGE_KEYS.USER_NAME))
  const userRole = ref<string | null>(localStorage.getItem(STORAGE_KEYS.USER_ROLE))
  const lastValidated = ref<number>(Number(localStorage.getItem(STORAGE_KEYS.LAST_VALIDATED)) || 0)
  const redirectUrl = ref<string | null>(localStorage.getItem(STORAGE_KEYS.REDIRECT_URL))

  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /**
   * Sperrzeitpunkt aus einer 423-Antwort, sonst null. Traegt den Countdown auf der
   * Anmeldemaske — ohne ihn erfaehrt der Trainer nicht, dass „später" 15 Minuten heisst.
   */
  const lockedUntil = ref<Date | null>(null)

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Angemeldet heisst jetzt: wir kennen den Nutzer. Ob das Cookie noch gilt, weiss nur der
   * Server — das klärt `validateSession()`. Diese Trennung ist offline zwingend: dort kann
   * niemand nachfragen, und die App muss trotzdem arbeiten.
   */
  const isAuthenticated = computed(() => userId.value !== null)

  const shouldValidateSession = computed(() => {
    if (userId.value === null) return false
    return Date.now() - lastValidated.value > VALIDATION_INTERVAL
  })

  const userInitials = computed(() => {
    if (!userName.value) return '?'
    const parts = userName.value.split(' ')
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    }
    return userName.value.substring(0, 2).toUpperCase()
  })

  // Aliases for backwards compatibility
  const trainerName = computed(() => userName.value)
  const role = computed(() => userRole.value)

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Login with username and password
   */
  async function login(credentials: LoginRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null
    lockedUntil.value = null

    try {
      // Clear previous user's cached data before setting new auth
      await clearAllUserData()
      await resetAllStores()

      const response = await apiClient.post<LoginResponse>('/Auth/login', credentials)
      setAuthData(response.data)
      return true
    } catch (e) {
      // `getErrorMessage` statt `e.message`: letzteres ist der Axios-Text
      // („Request failed with status code 401"), den der Trainer bis 2026-08-22 zu sehen bekam.
      error.value = getErrorMessage(e)
      lockedUntil.value = leseSperrzeitpunkt(e)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Login with credentials (alias for login with individual params)
   */
  async function loginWithCredentials(
    username: string,
    password: string,
    rememberMe = false
  ): Promise<boolean> {
    return login({ username, password, rememberMe })
  }

  /**
   * Initiate Microsoft OAuth login
   */
  function loginWithMicrosoft(returnUrl?: string): void {
    const callbackUrl = `${window.location.origin}/auth/callback`
    const finalReturnUrl = returnUrl || redirectUrl.value || '/dashboard'

    // Store return URL for callback handling
    localStorage.setItem(STORAGE_KEYS.REDIRECT_URL, finalReturnUrl)

    // Der Microsoft-Weg läuft über die API-Origin, nicht über den eigenen /api-Proxy:
    // ASP.NET baut die Rückadresse für Microsoft aus dem Host der Anfrage, und registriert
    // ist dort nur `https://sport.tus-wannsee.de/signin-microsoft`. Siehe VITE_AUTH_ORIGIN.
    const authBase = import.meta.env.VITE_AUTH_ORIGIN || import.meta.env.VITE_API_URL
    window.location.href = `${authBase}/MicrosoftAuth/login?returnUrl=${encodeURIComponent(callbackUrl)}`
  }

  /**
   * Validate current session
   */
  async function validateSession(): Promise<boolean> {
    try {
      const { data } = await apiClient.get<CurrentUserResponse>('/Auth/me')

      // Anzeigedaten nachziehen: Name oder Rolle können sich serverseitig geändert haben,
      // während das Gerät offline war.
      userId.value = data.userId
      personId.value = data.personId
      userName.value = data.fullName
      userRole.value = data.role
      schreibeNutzerdaten()

      lastValidated.value = Date.now()
      localStorage.setItem(STORAGE_KEYS.LAST_VALIDATED, String(lastValidated.value))

      return true
    } catch (e) {
      // Nur eine Absage des Servers beendet die Sitzung. Ein Netzfehler darf das NICHT tun —
      // sonst wirft die App den Trainer beim ersten Funkloch aus der Anwendung, obwohl seine
      // Sitzung völlig in Ordnung ist.
      if (isNetworkError(e)) return isAuthenticated.value

      clearAuthData()
      return false
    }
  }

  /**
   * Logout user - clears all auth data AND cached user data
   */
  async function logout(): Promise<void> {
    try {
      // Immer abmelden, auch wenn der lokale Zustand schon leer ist: nur der Server kann das
      // Cookie löschen und die Sitzung ungültig machen.
      await apiClient.post('/Auth/logout')
    } catch {
      // Ignore errors during logout
    } finally {
      clearAuthData()
      // Clear ALL cached data to prevent data leakage between users
      await resetAllStores()
      await clearAllUserData()
    }
  }

  /**
   * Clear all cached user data from IndexedDB
   * This prevents showing previous user's data after login
   */
  async function clearAllUserData(): Promise<void> {
    try {
      await db.clearAll()

      // Clear Service Worker runtime caches (StaleWhileRevalidate responses)
      // Without this, the SW serves a previous user's cached API responses
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames
            .filter((name) => name.endsWith('-api-cache'))
            .map((name) => caches.delete(name))
        )
      }

      console.warn('[AuthStore] Cleared all cached user data')
    } catch (e) {
      console.error('[AuthStore] Failed to clear cached data:', e)
    }
  }

  /**
   * Reset all data stores (Pinia in-memory state + IndexedDB cache)
   * Called on login and logout to ensure no stale data from a previous user is shown.
   * Must be awaited — courses store $reset is async (clears IndexedDB).
   */
  async function resetAllStores(): Promise<void> {
    await useCoursesStore().$reset()
    useSessionsStore().$reset()
    useRegistrationsStore().$reset()
    useAttendancesStore().$reset()
    useGuestsStore().$reset()
    useBillingStore().$reset()
    useParticipantsStore().$reset()
    useInstructorsStore().$reset()
    usePhotosStore().$reset()
  }

  /**
   * Set auth data from login response
   */
  function setAuthData(data: LoginResponse): void {
    userId.value = data.userId
    personId.value = data.personId
    userName.value = data.trainerName
    userRole.value = data.role
    lastValidated.value = Date.now()

    schreibeNutzerdaten()
    localStorage.setItem(STORAGE_KEYS.LAST_VALIDATED, String(lastValidated.value))

    // Clear redirect URL after successful login
    localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL)
    redirectUrl.value = null
  }

  /**
   * Schreibt die Anzeigedaten in den localStorage. Keine Geheimnisse — aber offline die einzige
   * Quelle dafür, wer angemeldet ist.
   */
  function schreibeNutzerdaten(): void {
    localStorage.setItem(STORAGE_KEYS.USER_ID, String(userId.value))
    if (personId.value != null) {
      localStorage.setItem(STORAGE_KEYS.PERSON_ID, String(personId.value))
    } else {
      localStorage.removeItem(STORAGE_KEYS.PERSON_ID)
    }
    localStorage.setItem(STORAGE_KEYS.USER_NAME, userName.value ?? '')
    localStorage.setItem(STORAGE_KEYS.USER_ROLE, userRole.value ?? '')
  }

  /**
   * Clear all auth data
   */
  function clearAuthData(): void {
    userId.value = null
    personId.value = null
    userName.value = null
    userRole.value = null
    lastValidated.value = 0
    error.value = null
    lockedUntil.value = null

    // Clear localStorage
    Object.values(STORAGE_KEYS).forEach((key) => {
      if (key !== STORAGE_KEYS.REDIRECT_URL) {
        localStorage.removeItem(key)
      }
    })
    entferneAltlasten()
  }

  /**
   * Set redirect URL for after login
   */
  function setRedirectUrl(url: string): void {
    redirectUrl.value = url
    localStorage.setItem(STORAGE_KEYS.REDIRECT_URL, url)
  }

  /**
   * Get and clear redirect URL
   */
  function consumeRedirectUrl(): string {
    const url = redirectUrl.value || '/dashboard'
    redirectUrl.value = null
    localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL)
    return url
  }

  /**
   * Reset store (for testing)
   */
  function $reset(): void {
    clearAuthData()
    isLoading.value = false
  }

  return {
    // State
    userId,
    personId,
    userName,
    userRole,
    isLoading,
    error,
    lockedUntil,
    redirectUrl,

    // Getters
    isAuthenticated,
    shouldValidateSession,
    userInitials,
    trainerName,
    role,

    // Actions
    login,
    loginWithCredentials,
    loginWithMicrosoft,
    validateSession,
    logout,
    setAuthData,
    setRedirectUrl,
    consumeRedirectUrl,
    $reset,
  }
})
